/**
 * Daily Health Tips - data + selector (kept separate from the UI on purpose).
 *
 * 5 categories x 6 tips = 30 tips. One tip is shown per calendar day.
 * To change wording, edit the `tips` arrays below. Keep each tip short
 * (about 65 characters or fewer) so it fits on one line on desktop.
 */

export type HealthTipCategoryId =
  | 'water'
  | 'exercise'
  | 'mindfulness'
  | 'nutrition'
  | 'sleep';

export interface HealthTipCategory {
  id: HealthTipCategoryId;
  /** Text shown in the category badge */
  label: string;
  /**
   * Path of the decorative background visual for this category.
   * TODO: point each entry at the EXISTING Health Insight asset for that theme.
   */
  background: string;
  /** Exactly 6 tips per category */
  tips: string[];
}

export interface DailyHealthTip {
  categoryId: HealthTipCategoryId;
  category: string;
  tip: string;
  background: string;
}

const TIP_BG_BASE = 'assets/images/health/tips/';

export const HEALTH_TIP_CATEGORIES: HealthTipCategory[] = [
  {
    id: 'water',
    label: 'Water Intake',
    background: TIP_BG_BASE + 'water.svg',
    tips: [
      'Keep water nearby during the day to make regular hydration easier.',
      'Start your morning with a glass of water.',
      'Pair a glass of water with each of your meals.',
      'Carry a refillable bottle with you when you head out.',
      'Notice when you feel thirsty and take a few sips of water.',
      'Add a slice of lemon or a few mint leaves if plain water feels dull.',
    ],
  },
  {
    id: 'exercise',
    label: 'Exercise',
    background: TIP_BG_BASE + 'exercise.svg',
    tips: [
      'Add a short walk to your day whenever you have a chance to move.',
      'Take the stairs when it is practical and comfortable for you.',
      'Stretch gently for a few minutes after sitting for a while.',
      'Choose an activity you enjoy so staying active feels easier.',
      'Stand up and move around for a minute during long periods of sitting.',
      'Start at a pace that feels comfortable and build up gradually.',
    ],
  },
  {
    id: 'mindfulness',
    label: 'Yoga & Mindfulness',
    background: TIP_BG_BASE + 'mindfulness.svg',
    tips: [
      'Take a few minutes for slow breathing to create a calmer moment.',
      'Try a gentle stretch and notice how your body feels.',
      'Pause between tasks and take three slow, unhurried breaths.',
      'Spend a quiet minute noticing what you see and hear around you.',
      'Relax your shoulders and soften your jaw as you go about your day.',
      'End your day with a few quiet minutes of reflection.',
    ],
  },
  {
    id: 'nutrition',
    label: 'Nutrition',
    background: TIP_BG_BASE + 'nutrition.svg',
    tips: [
      'Try adding a variety of colourful fruits and vegetables to your meals.',
      'Aim for a balanced plate with a mix of different food groups.',
      'Eat slowly and pay attention to how full you feel.',
      'Keep a simple, wholesome snack within reach for busy days.',
      'Try whole grains in place of refined grains where you can.',
      'Read food labels to know more about what goes into your meals.',
    ],
  },
  {
    id: 'sleep',
    label: 'Sleep & Recovery',
    background: TIP_BG_BASE + 'sleep.svg',
    tips: [
      'Keep a consistent sleep routine to support healthy rest.',
      'Wind down with a quiet activity before bedtime.',
      'Keep your bedroom dark, cool and comfortable for sleep.',
      'Try to limit screen time shortly before you go to bed.',
      'Give yourself time to rest and recover after a busy day.',
      'Get some daylight in the morning to help set your body clock.',
    ],
  },
];

/** Length of the full cycle: 5 categories x 6 tips. */
export const HEALTH_TIP_CYCLE_LENGTH = 30;

/**
 * Deterministic tip for a given LOCAL calendar date.
 *
 * - Uses the local y/m/d, converted to a whole-day number (immune to DST and
 *   continuous across year boundaries), then `% 30`.
 * - Consecutive days rotate through the categories (day % 5) and only move on
 *   to the next tip of a category every 5 days, so the same category never
 *   appears two days in a row and all 30 tips are used once per cycle.
 */
export function getDailyHealthTip(date: Date = new Date()): DailyHealthTip {
  const dayNumber = Math.floor(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()) / 86400000
  );
  const slot = dayNumber % HEALTH_TIP_CYCLE_LENGTH;
  const category = HEALTH_TIP_CATEGORIES[slot % HEALTH_TIP_CATEGORIES.length];
  const tip = category.tips[Math.floor(slot / HEALTH_TIP_CATEGORIES.length)];

  return {
    categoryId: category.id,
    category: category.label,
    tip,
    background: category.background,
  };
}
